import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DetailsCard } from "components";
import { MileageChart } from "modules/CarDetails/components";
import "modules/CarDetails/components/CarDrivingTrend/CarDrivingTrend.scss";
import { getAverageKM, getColorByAverageKM } from "modules/CarDetails/utils";

import type { SectionMenu } from "components";
import type { Appointment } from "models";

type CarDrivingTrendProps = {
  sectionMenu: SectionMenu;
  isExpanded: boolean;
  appointments?: Appointment[];
};

type KmChartColors = {
  backgroundColors: string[];
};

export type BubbleData = {
  x: Date;
  y: number;
  r: number;
};

type RelevantData = {
  km: number;
  time: Date;
};

type CarDrivingChartData = {
  isMilageChartVisible: boolean;
  totalAverageKM: {
    color: string;
    day: number;
    month: number;
    year: number;
  };
  kmChartColors: {
    backgroundColors: string[];
  };
  bubbleData: BubbleData[];
  lineData: BubbleData[];
};

const prepareChartData = (history?: Appointment[]): CarDrivingChartData | null => {
  if (!history || history.length < 2) return null;

  const lineData: BubbleData[] = [];
  const bubbleData: BubbleData[] = [];
  const relevantDataForAverageCalculation: RelevantData[] = [];
  const kmChartColors: KmChartColors = { backgroundColors: [] };
  let currentKm = -1;

  [...history].reverse().forEach(item => {
    const date = new Date(item.time_car_app);

    if (item.current_km > currentKm) {
      currentKm = item.current_km;
      kmChartColors.backgroundColors.push("#2D4473");
      lineData.push({ x: date, y: item.current_km, r: 6 });
      relevantDataForAverageCalculation.push({ km: item.current_km, time: date });
    } else {
      kmChartColors.backgroundColors.push("#FF0000");
    }

    bubbleData.push({ x: date, y: item.current_km, r: 6 });
  });

  if (relevantDataForAverageCalculation.length < 2) return null;

  const totalAverageKM = getAverageKM(relevantDataForAverageCalculation[0], relevantDataForAverageCalculation[1]);

  return {
    isMilageChartVisible: true,
    kmChartColors,
    bubbleData,
    lineData,
    totalAverageKM: {
      color: getColorByAverageKM(totalAverageKM),
      day: totalAverageKM,
      month: 30 * totalAverageKM,
      year: 365 * totalAverageKM
    }
  };
};

export const hasDrivingTrend = (appointments?: Appointment[]) => !!prepareChartData(appointments);

export const CarDrivingTrend = ({ appointments, sectionMenu, isExpanded }: CarDrivingTrendProps) => {
  const { t } = useTranslation();

  const [chartData, setChartData] = useState<CarDrivingChartData | null>(null);

  useEffect(() => setChartData(prepareChartData(appointments)), [appointments]);

  if (!chartData) return null;

  return (
    <DetailsCard isExpanded={isExpanded} sectionMenuId={sectionMenu.id} icon={sectionMenu.icon} title={sectionMenu.title}>
      <div className="CarDrivingTrend">
        <div className="MileageChart-Wrapper">
          <div className="DrivingTrendAverages">
            {t("v8_total_car_driving_trend", "Total driving trend")} ({t("v8_average", "Average")}&nbsp;
            <span style={{ color: chartData.totalAverageKM.color }}>{chartData.totalAverageKM.day}</span> km {t("v8_per_day", "per day")},&nbsp;
            <span style={{ color: chartData.totalAverageKM.color }}>{chartData.totalAverageKM.month}</span> km {t("v8_per_month", "per month")},&nbsp;
            <span style={{ color: chartData.totalAverageKM.color }}>{chartData.totalAverageKM.year}</span> km {t("v8_per_year", "per year")} )
          </div>
          <MileageChart colors={chartData.kmChartColors} bubbleData={chartData.bubbleData} lineData={chartData.lineData} />
        </div>
      </div>
    </DetailsCard>
  );
};
